// MF-568 — Card 'pat-progress-flows' (2C): sankey flows from the previous result to the latest, for achievement bands
// and for stanines, over the same matched students. Click a ribbon (or its button below) to list who moved. Students
// with a single result are listed apart. Rows: same shape as 'pat-progress-pairs'.
import { DetailCard, DetailTable } from './PatDetail'
import { useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { Box, Typography } from '@mui/material'
import { SUMMARY_PALETTE, FONT, baseTextStyle, tooltipBase } from '../boardTheme'
import { benchmarkColor } from '../boardPalettes'
import {
  STANINE_NOTE,
  byBand,
  isNum,
  fmtChange,
  fmtDate,
  elapsedText,
  splitPairs,
  changeOf,
  useChartResize
} from './patProgressShared'

const MEASURES = {
  band: {
    title: 'Achievement bands',
    cat: (r, side) => (side === 'prev' ? r.prev_band : r.band),
    order: byBand
  },
  stanine: {
    title: 'Stanines',
    cat: (r, side) => (side === 'prev' ? r.prev_stanine : r.stanine),
    order: (a, b) => Number(a) - Number(b)
  }
}

/** students → { nodes, links, counts } for one measure; categories are ordered high → low (top → bottom). */
const buildFlow = (students, measure) => {
  const m = MEASURES[measure]
  const cats = [...new Set(students.flatMap((r) => [m.cat(r, 'prev'), m.cat(r, 'now')]).map(String))].sort(
    m.order
  )
  const rank = (c) => (cats.length > 1 ? cats.indexOf(c) / (cats.length - 1) : 0.5)
  const count = new Map()
  const links = new Map()
  for (const r of students) {
    const from = `prev:${m.cat(r, 'prev')}`
    const to = `now:${m.cat(r, 'now')}`
    count.set(from, (count.get(from) || 0) + 1)
    count.set(to, (count.get(to) || 0) + 1)
    links.set(`${from}|${to}`, (links.get(`${from}|${to}`) || 0) + 1)
  }
  const nodes = []
  for (const side of ['prev', 'now']) {
    for (const c of [...cats].reverse()) {
      const id = `${side}:${c}`
      if (!count.has(id)) continue // omit empty categories
      nodes.push({ id, cat: c, n: count.get(id), color: benchmarkColor(rank(c) * 100) })
    }
  }
  return {
    nodes,
    links: [...links.entries()].map(([k, value]) => {
      const [source, target] = k.split('|')
      return { source, target, value }
    })
  }
}

const FlowChart = ({ measure, students, onPick, picked }) => {
  const { chartRef, boxRef } = useChartResize()
  const flow = useMemo(() => buildFlow(students, measure), [students, measure])
  const label = (id) => id.slice(id.indexOf(':') + 1)
  const unit = measure === 'band' ? 'Band' : 'Stanine'
  const option = useMemo(
    () => ({
      textStyle: baseTextStyle,
      animationDuration: 300,
      tooltip: {
        ...tooltipBase,
        trigger: 'item',
        formatter: (p) =>
          p.dataType === 'edge'
            ? `${unit} ${label(p.data.source)} → ${label(p.data.target)}<br/><b>${p.data.value}</b> student${p.data.value === 1 ? '' : 's'}`
            : `${unit} ${label(p.data.name)}: <b>${p.value}</b> student${p.value === 1 ? '' : 's'}`
      },
      series: [
        {
          type: 'sankey',
          left: 8,
          right: 110,
          top: 8,
          bottom: 8,
          nodeWidth: 14,
          nodeGap: 10,
          layoutIterations: 0,
          emphasis: { focus: 'adjacency' },
          lineStyle: { color: 'gradient', opacity: 0.35, curveness: 0.5 },
          label: {
            fontFamily: FONT,
            fontSize: 11,
            color: SUMMARY_PALETTE.ink,
            formatter: (p) => `${label(p.data.name)} · ${p.value}`
          },
          data: flow.nodes.map((n) => ({ name: n.id, itemStyle: { color: n.color } })),
          links: flow.links
        }
      ]
    }),
    [flow, unit]
  )
  const height = Math.max(
    220,
    Math.max(...['prev', 'now'].map((s) => flow.nodes.filter((n) => n.id.startsWith(s)).length)) * 34
  )
  return (
    <Box sx={{ flex: '1 1 340px', minWidth: 0 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: SUMMARY_PALETTE.ink }}>
        {MEASURES[measure].title}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 11,
          color: SUMMARY_PALETTE.muted,
          pr: '110px',
          pl: 1
        }}
      >
        <span>Previous result</span>
        <span>Latest result</span>
      </Box>
      <div ref={boxRef} style={{ height, minWidth: 0 }}>
        <ReactECharts
          ref={chartRef}
          option={option}
          notMerge
          style={{ height: '100%', width: '100%' }}
          onEvents={{
            click: (p) => {
              if (p.dataType === 'edge') onPick({ measure, source: p.data.source, target: p.data.target })
            }
          }}
        />
      </div>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
        {flow.links.map((l) => {
          const active =
            picked && picked.measure === measure && picked.source === l.source && picked.target === l.target
          return (
            <Box
              key={`${l.source}|${l.target}`}
              component='button'
              type='button'
              aria-pressed={!!active}
              onClick={() => onPick({ measure, source: l.source, target: l.target })}
              sx={{
                fontFamily: FONT,
                fontSize: 11,
                px: 1,
                py: 0.25,
                borderRadius: '12px',
                cursor: 'pointer',
                border: `1px solid ${active ? SUMMARY_PALETTE.good : SUMMARY_PALETTE.cardBorder}`,
                bgcolor: active ? '#eef3f9' : '#fff',
                color: SUMMARY_PALETTE.ink
              }}
            >
              {label(l.source)} → {label(l.target)}: {l.value}
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

const PatProgressFlowsCard = ({ data }) => {
  const rows = useMemo(() => data?.rows || [], [data])
  const { matched, single } = useMemo(() => splitPairs(rows), [rows])
  const [picked, setPicked] = useState(null)
  const withStanine = useMemo(
    () => matched.filter((r) => isNum(r.stanine) && isNum(r.prev_stanine)),
    [matched]
  )
  const showStanine = withStanine.length > 0
  // the band flow has every matched student; the stanine flow only those with a stanine on both results
  const students = matched
  const omitted = matched.length - withStanine.length
  const studentsOf = (measure) => (measure === 'stanine' ? withStanine : matched)

  const list = useMemo(() => {
    if (!picked) return []
    const m = MEASURES[picked.measure]
    const cut = (id) => id.slice(id.indexOf(':') + 1)
    return (picked.measure === 'stanine' ? withStanine : matched)
      .filter(
        (r) =>
          String(m.cat(r, 'prev')) === cut(picked.source) && String(m.cat(r, 'now')) === cut(picked.target)
      )
      .sort((a, b) => changeOf(b) - changeOf(a) || a.name.localeCompare(b.name))
  }, [picked, matched, withStanine])

  const measures = showStanine ? ['band', 'stanine'] : ['band']
  const [lo, hi] = [Math.min(...students.map(changeOf)), Math.max(...students.map(changeOf))]

  return (
    <Box sx={{ height: '100%', overflow: 'auto', minWidth: 0, fontFamily: FONT }}>
      {students.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {measures.map((m) => (
            <FlowChart key={m} measure={m} students={studentsOf(m)} onPick={setPicked} picked={picked} />
          ))}
        </Box>
      )}

      {!showStanine && matched.length > 0 && (
        <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted, mt: 1 }}>{STANINE_NOTE}</Typography>
      )}
      {showStanine && omitted > 0 && (
        <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted, mt: 1 }}>
          The stanine flow has {withStanine.length} of {matched.length} matched students: {omitted} have no
          stanine on one of the two results (a stanine needs the school&apos;s norm for the year level of each
          result).
        </Typography>
      )}

      {picked && (
        <DetailCard
          title={`${MEASURES[picked.measure].title}: ${picked.source.slice(picked.source.indexOf(':') + 1)} → ${picked.target.slice(picked.target.indexOf(':') + 1)}`}
          count={list.length}
          onClear={() => setPicked(null)}
        >
          <DetailTable
            rowKey={(r) => r.student_id}
            rows={list}
            columns={[
              { key: 'name', label: 'Student', wrap: true },
              { key: 'scores', label: 'Score', render: (r) => `${r.prev_score} → ${r.score}` },
              {
                key: 'change',
                label: 'Change',
                align: 'right',
                bold: true,
                render: (r) => {
                  const d = changeOf(r)
                  return (
                    <Box
                      component='span'
                      sx={{
                        color:
                          d > 0 ? SUMMARY_PALETTE.good : d < 0 ? SUMMARY_PALETTE.bad : SUMMARY_PALETTE.faint
                      }}
                    >
                      {fmtChange(d)}
                    </Box>
                  )
                }
              },
              {
                key: 'dates',
                label: 'Tested',
                muted: true,
                render: (r) => {
                  const gap = elapsedText(r.prev_test_date, r.test_date)
                  return `${fmtDate(r.prev_test_date)} → ${fmtDate(r.test_date)}${gap ? ` (${gap})` : ''}`
                }
              }
            ]}
          />
        </DetailCard>
      )}

      {single.length > 0 && (
        <Box sx={{ mt: 1.5, fontSize: 12, color: SUMMARY_PALETTE.muted }}>
          <b>
            Only one result, not in the flows: {single.length} student{single.length === 1 ? '' : 's'}
          </b>
          {' — '}
          {single.map((r) => r.name).join(', ')}
        </Box>
      )}

      <Typography sx={{ fontSize: 11, color: SUMMARY_PALETTE.faint, mt: 1.5, lineHeight: 1.5 }}>
        Ribbon width measures students, not the amount of learning. Only students with a previous result are
        matched
        {students.length > 0 && Number.isFinite(lo)
          ? ` (observed change ${fmtChange(lo)} to ${fmtChange(hi)})`
          : ''}
        ; year level and norm version can differ between the two results.
      </Typography>
    </Box>
  )
}

export default PatProgressFlowsCard
